import React from 'react';
import { Box, Dialog, IconButton, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import { getStyleVariables, opacities } from 'styles/vars';
import PanHandIcon from '../../icons/PanHandIcon/PanHandIcon';
import TextMatchCursorIcon from '../../icons/TextMatchCursorIcon/TextMatchCursorIcon';
import CommandKeyIcon, { COMMAND_KEY_ICON_SIZE } from '../../icons/CommandKeyIcon/CommandKeyIcon';

interface KeyboardShortcutsModalProps {
  open: boolean;
  onClose: () => void;
}

type KeyToken = string | { icon: React.ComponentType<SvgIconProps> };

// Compared by identity in renderKeys, so it must stay a single shared instance.
const CMD: KeyToken = { icon: CommandKeyIcon };

const CMD_ICON_SIZE = { width: COMMAND_KEY_ICON_SIZE, height: COMMAND_KEY_ICON_SIZE };

const FOOTER_COLOR = 'var(--lightgrey-100, #8792AB)';
const ROW_BAND_COLOR = '#333539';
const MODAL_Z_INDEX = 10000;

interface Shortcut {
  icon: React.ComponentType<SvgIconProps>;
  iconColor: string;
  flipIcon?: boolean;
  iconSize?: { width: string; height: string };
  label: string;
  mac: KeyToken[];
  pc: KeyToken[];
  note?: string;
}

const useStyles = makeStyles()((theme) => {
  const { colors } = getStyleVariables(theme);
  return {
    paper: {
      width: '526px',
      maxWidth: '526px',
      borderRadius: '6px',
      padding: '16px 24px',
      backgroundColor: colors.darkGreyBackground,
      color: colors.primaryTextColor,
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
    },
    title: {
      color: colors.primaryTextColor,
      fontSize: '18px',
      lineHeight: '26px',
      fontWeight: 600,
    },
    closeButton: {
      color: colors.primaryTextColor,
      padding: 0,
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '0 12px 8px 0',
      color: alpha(theme.palette.primary.main, 0.65),
      fontSize: '12px',
    },
    rows: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      backgroundColor: colors.darkGreyBackground,
    },
    row: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '0 12px 0 10px',
      borderRadius: '4px',
      backgroundColor: ROW_BAND_COLOR,
    },
    rowLine: {
      display: 'flex',
      alignItems: 'center',
      minHeight: '36px',
    },
    lastRow: {
      paddingBottom: '8px',
    },
    descCell: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '12px',
      color: colors.primaryTextColor,
    },
    keyCell: {
      flexShrink: 0,
      width: '117px',
      display: 'flex',
      alignItems: 'center',
    },
    macCell: {
      justifyContent: 'flex-end',
      paddingRight: '5px',
    },
    pcCell: {
      justifyContent: 'flex-end',
      paddingRight: '5px',
      marginLeft: '24px',
    },
    iconSlot: {
      width: '18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    icon: {
      fontSize: '18px',
      flexShrink: 0,
      '& path': {
        fill: 'currentColor',
      },
    },
    keyCap: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '19px',
      minWidth: '20px',
      padding: '0 6px 0 7px',
      boxSizing: 'border-box',
      border: `1px solid ${alpha(theme.palette.primary.main, opacities.disabled)}`,
      borderRadius: '4px',
      backgroundColor: colors.menuLightGrey,
      fontSize: '12px',
      lineHeight: 1,
      fontWeight: 500,
      color: colors.primaryTextColor,
    },
    cmdCap: {
      width: '18px',
      minWidth: '18px',
      height: '18px',
      padding: 0,
    },
    keyGlyph: {
      display: 'block',
      fontSize: '14px',
    },
    plus: {
      margin: '0 4px',
      color: alpha(theme.palette.primary.main, 0.5),
      fontSize: '12px',
    },
    footer: {
      marginTop: '2px',
      color: FOOTER_COLOR,
      fontSize: '10px',
      fontWeight: 600,
      lineHeight: '16px',
      letterSpacing: 0,
    },
  };
});

const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({ open, onClose }) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const { colors } = getStyleVariables(theme);
  const iconLight = alpha(theme.palette.primary.main, 0.85);

  const shortcuts: Shortcut[] = [
    { icon: ThumbUpOffAltIcon, iconColor: '#19CAD7', label: 'Accept', mac: ['F1'], pc: ['F1'] },
    { icon: ThumbDownOffAltIcon, iconColor: colors.red, label: 'Reject', mac: ['F2'], pc: ['F2'] },
    { icon: VisibilityOffOutlinedIcon, iconColor: '#5586F1', label: 'Ignore', mac: ['F3'], pc: ['F3'] },
    {
      icon: ChatBubbleOutlineOutlinedIcon,
      iconColor: iconLight,
      flipIcon: true,
      label: 'Comment',
      mac: [CMD, 'M'],
      pc: ['Ctrl', 'M'],
    },
    {
      icon: ArrowUpwardIcon,
      iconColor: iconLight,
      label: 'Previous difference',
      mac: [{ icon: ArrowUpwardIcon }],
      pc: [{ icon: ArrowUpwardIcon }],
    },
    {
      icon: ArrowDownwardIcon,
      iconColor: iconLight,
      label: 'Next difference',
      mac: [{ icon: ArrowDownwardIcon }],
      pc: [{ icon: ArrowDownwardIcon }],
    },
    {
      icon: ZoomInOutlinedIcon,
      iconColor: iconLight,
      iconSize: { width: '22px', height: '22px' },
      label: 'Zoom in/out',
      mac: [CMD, 'Scroll'],
      pc: ['Ctrl', 'Scroll'],
    },
    { icon: PanHandIcon, iconColor: iconLight, label: 'Pan', mac: ['Space'], pc: ['Space'] },
    {
      icon: TextMatchCursorIcon,
      iconColor: '#75E26D',
      label: 'See text match',
      mac: [CMD, 'Click'],
      pc: ['Ctrl', 'Click'],
      note: 'Hold key down while clicking on a word to see the text match in the other document.',
    },
  ];

  const renderKeys = (keys: KeyToken[]) =>
    keys.map((key, index) => {
      const isCmd = key === CMD;
      let content: React.ReactNode;
      if (typeof key === 'string') {
        content = key;
      } else {
        const Glyph = key.icon;
        content = <Glyph className={classes.keyGlyph} sx={isCmd ? CMD_ICON_SIZE : undefined} />;
      }
      return (
        <React.Fragment key={index}>
          {index > 0 && <span className={classes.plus}>+</span>}
          <span className={cx(classes.keyCap, isCmd && classes.cmdCap)}>{content}</span>
        </React.Fragment>
      );
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby="keyboard-shortcuts-title"
      PaperProps={{ className: classes.paper }}
      sx={{ zIndex: MODAL_Z_INDEX }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography id="keyboard-shortcuts-title" className={classes.title}>
          Keyboard shortcuts
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          className={classes.closeButton}
          data-testid="keyboard-shortcuts-close-button"
          aria-label="Close keyboard shortcuts"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.content}>
        <Box className={classes.headerRow}>
          <span style={{ flex: 1 }}>Description</span>
          <span style={{ width: '117px', textAlign: 'right', paddingRight: '12px' }}>Mac</span>
          <span style={{ width: '117px', textAlign: 'right', paddingRight: '12px', marginLeft: '24px' }}>PC</span>
        </Box>

        <Box className={classes.rows}>
          {shortcuts.map((shortcut) => {
            const Icon = shortcut.icon;
            return (
              <Box className={cx(classes.row, shortcut.note && classes.lastRow)} key={shortcut.label}>
                <Box className={classes.rowLine}>
                  <Box className={classes.descCell}>
                    <Box className={classes.iconSlot}>
                      <Icon
                        className={classes.icon}
                        sx={{
                          color: shortcut.iconColor,
                          ...(shortcut.flipIcon && { transform: 'scaleX(-1)' }),
                          ...(shortcut.iconSize && {
                            width: shortcut.iconSize.width,
                            height: shortcut.iconSize.height,
                          }),
                        }}
                      />
                    </Box>
                    {shortcut.label}
                  </Box>
                  <Box className={cx(classes.keyCell, classes.macCell)}>{renderKeys(shortcut.mac)}</Box>
                  <Box className={cx(classes.keyCell, classes.pcCell)}>{renderKeys(shortcut.pc)}</Box>
                </Box>
                {shortcut.note && <Typography className={classes.footer}>{shortcut.note}</Typography>}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Dialog>
  );
};

export default KeyboardShortcutsModal;
